import { Component, OnInit, computed, input, signal } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';

import {
  PatientProgramModel,
  PatientTargetModel,
  StaffTargetComplianceReportModel,
} from '../../core/api/models';
import { GraphApiService, StaffApiService, SupervisorApiService } from '../../core/api/services';
import { AlertService } from '../../shared/services/alert.service';
import { SupervisorHelperService } from '../supervisor.helper.service';
import { SupervisorBaseComponent } from '../supervisor-base.component';
import { AuthService } from '../../auth/auth.service';
import { PatientDataFacadeService } from '../../core/services/patient-data-facade.service';
import { getPatientTargetStatuses } from '../../shared/utils/utils';
import * as FileSaver from 'file-saver';

@Component({
  selector: 'app-staff-management-report',
  templateUrl: './staff-management-report.component.html',
  styleUrls: ['./staff-management-report.component.css'],
  standalone: false,
})
export class StaffManagementReportComponent
  extends SupervisorBaseComponent
  implements OnInit
{
  patientID = input.required<string>();

  // Filters
  selectedProgramIDs = signal<number[]>([]);
  selectedTargetIDs = signal<number[]>([]);
  selectedTargetStatusIDs = signal<number[]>([]);
  selectedStaffIDs = signal<number[]>([]);
  targetStatusOptions = getPatientTargetStatuses();

  // Date range (default: last 30 days)
  startDate = signal<string>(
    new Date(new Date().setDate(new Date().getDate() - 30))
      .toISOString()
      .substring(0, 10)
  );
  endDate = signal<string>(
    new Date().toISOString().substring(0, 10)
  );

  // Report data
  report = signal<StaffTargetComplianceReportModel | null>(null);

  // Convenience computed properties
  programs = computed<PatientProgramModel[]>(() =>
    this.supervisorHelperService.programs().filter((p) => !p.IsDeleted && !p.IsArchived) ?? []
  );
  targets = computed<PatientTargetModel[]>(() =>
    this.supervisorHelperService.targets().filter((t) => !t.IsDeleted && !t.IsArchived) ?? []
  );

  // Staff options built from all staff present in the (unfiltered-by-staff) report
  staffOptions = computed(() => {
    const rows = this.report()?.Rows ?? [];
    const byId: { [id: number]: string } = {};

    for (const row of rows) {
      if (row.StaffID != null && row.StaffName) {
        byId[row.StaffID] = row.StaffName;
      }
    }

    return Object.keys(byId)
      .map((id) => ({
        label: byId[+id],
        value: +id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  });

  // Rows filtered by selected staff IDs (client-side), so the API is always called
  // without a staff filter and staffOptions can list all staff.
  filteredRows = computed(() => {
    const currentReport = this.report();
    const rows = currentReport?.Rows ?? [];

    const staffFilter = this.selectedStaffIDs();
    if (!staffFilter || staffFilter.length === 0) {
      return rows;
    }

    const staffSet = new Set(staffFilter);
    return rows.filter(
      (row) =>
        row.StaffID != null && staffSet.has(row.StaffID)
    );
  });

  isLoadingReport = signal<boolean>(false);

  constructor(
    protected override supervisorApiService: SupervisorApiService,
    protected override alertService: AlertService,
    protected override supervisorHelperService: SupervisorHelperService,
    protected override authService: AuthService,
    protected override staffApiService: StaffApiService,
    private graphApiService: GraphApiService,
    private route: ActivatedRoute,
    private router: Router,
    patientDataFacadeService: PatientDataFacadeService
  ) {
    super(
      supervisorApiService,
      alertService,
      supervisorHelperService,
      authService,
      staffApiService,
      patientDataFacadeService
    );
  }

  ngOnInit(): void {
    const patientIdNumber = Number(this.patientID());

    // Load supervisor base data (programs, targets, etc.) then initialise filters and report
    this.isLoading = true;
    this.loadBaseData(patientIdNumber, () => {
      this.isLoading = false;
      this.initFromQueryParams();
      this.loadReport();
    });
  }

  private initFromQueryParams() {
    this.route.queryParams.subscribe((params) => {
      const programID = params['programID']
        ? Number(params['programID'])
        : null;
      const targetID = params['targetID']
        ? Number(params['targetID'])
        : null;
      const staffID = params['staffID']
        ? Number(params['staffID'])
        : null;

      if (programID) {
        this.selectedProgramIDs.set([programID]);
      }

      if (targetID) {
        this.selectedTargetIDs.set([targetID]);
      }

      if (staffID) {
        this.selectedStaffIDs.set([staffID]);
      }
    });
  }

  loadReport() {
    const patientIdNumber = Number(this.patientID());

    if (!patientIdNumber) {
      return;
    }

    const start = new Date(this.startDate());
    const end = new Date(this.endDate());

    // Normalise end date to end of day
    end.setHours(23, 59, 59, 999);

    this.isLoadingReport.set(true);

    this.graphApiService
      .GetStaffTargetComplianceReport({
        patientID: patientIdNumber,
        startTime: start.toISOString(),
        endTime: end.toISOString(),
        programIDs: this.selectedProgramIDs(),
        targetIDs: this.selectedTargetIDs(),
        targetStatusIDs: this.selectedTargetStatusIDs(),
      })
      .subscribe({
        next: (report) => {
          this.report.set(report);
          this.isLoadingReport.set(false);
        },
        error: () => {
          this.isLoadingReport.set(false);
          this.alertService.error(
            'Error loading staff management report. Please try again.'
          );
        },
      });
  }

  clearFilters() {
    this.selectedProgramIDs.set([]);
    this.selectedTargetIDs.set([]);
    this.selectedTargetStatusIDs.set([]);
    this.selectedStaffIDs.set([]);
  }

  getCellText(rowIndex: number, columnIndex: number): string {
    const currentReport = this.report();
    if (!currentReport || !currentReport.Rows || !currentReport.Columns) {
      return '';
    }

    const row = currentReport.Rows[rowIndex];
    const col = currentReport.Columns[columnIndex];

    if (!row || !col || !row.Targets) {
      return '';
    }

    const cell = row.Targets.find(
      (t) => t.TargetID === col.TargetID
    );

    const desired = cell?.DesiredTrials ?? col.DesiredTrials ?? 0;
    const count = cell?.TrialCount ?? 0;

    if (!desired) {
      return count > 0 ? `${count}` : '-';
    }

    return `${count}/${desired}`;
  }

  isUnderTarget(rowIndex: number, columnIndex: number): boolean {
    const currentReport = this.report();
    if (!currentReport || !currentReport.Rows || !currentReport.Columns) {
      return false;
    }

    const row = currentReport.Rows[rowIndex];
    const col = currentReport.Columns[columnIndex];

    if (!row || !col || !row.Targets) {
      return false;
    }

    const cell = row.Targets.find(
      (t) => t.TargetID === col.TargetID
    );

    const desired = cell?.DesiredTrials ?? col.DesiredTrials ?? 0;
    const count = cell?.TrialCount ?? 0;

    return desired > 0 && count < desired;
  }

  exportToExcel() {
    const currentReport = this.report();
    const rows = this.filteredRows();

    if (!currentReport || !currentReport.Columns || !rows || rows.length === 0) {
      this.alertService.error('There is no data to export.');
      return;
    }

    const allRows = currentReport.Rows ?? [];
    const columns = currentReport.Columns ?? [];

    const baseHeaders = ['Session Date', 'Staff'];
    const targetHeaders = columns.map((col, colIndex) =>
      col.TargetName ??
      (col.TargetID != null ? `Target ${col.TargetID}` : `Target ${colIndex + 1}`)
    );
    const headers = [...baseHeaders, ...targetHeaders];

    const data = rows.map((row) => {
      const rowIndex = allRows.indexOf(row);

      const result: any = {};

      // Fixed columns
      result['Session Date'] = row.SessionStartedDate
        ? new Date(row.SessionStartedDate).toLocaleDateString('en-US')
        : '';
      result['Staff'] = row.StaffName ?? '';

      // Dynamic target columns
      columns.forEach((col, colIndex) => {
        const header = targetHeaders[colIndex];

        const value =
          rowIndex >= 0 ? this.getCellText(rowIndex, colIndex) : '';

        (result as any)[header] = value;
      });

      return result;
    });

    import('xlsx').then((xlsx) => {
      const worksheet = xlsx.utils.json_to_sheet(data, { header: headers });
      const workbook = { Sheets: { data: worksheet }, SheetNames: ['data'] };
      const excelBuffer: any = xlsx.write(workbook, {
        bookType: 'xlsx',
        type: 'array',
      });

      const blob = new Blob([excelBuffer], {
        type:
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
      });

      const timestamp = new Date()
        .toISOString()
        .slice(0, 19)
        .replace(/[:T]/g, '-');
      const filename = `staff-management-report-${timestamp}.xlsx`;

      FileSaver.saveAs(blob, filename);
    });
  }
}



